import { Box, MenuItem, TextField } from "@mui/material";
import { updateServiceDescription } from "api/services/serviceDescription";
import DialogWrapper from "components/DialogWrapper";
import LoadingButton from "components/LoadingButton";
import SafeTextField from "components/SafeTextField";
import { snack } from "components/toast";
import { TAXES } from "data/taxes";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { DialogProps, SubmitType } from "types";
import { handleError } from "utils/handleError";

interface ServiceDescriptionRow {
  description: string;
  hsnSac: string;
  gstRate: string;
  serviceFee: string;
}

interface Props extends DialogProps {
  data: any;
  onUpdated?: () => void;
}

const emptyRow: ServiceDescriptionRow = { description: "", hsnSac: "", gstRate: "", serviceFee: "" };

const HSN_SAC_REGEX = /^\d{2}$|^\d{4}$|^\d{6}$/;
const isValidHsnSac = (value: string) => HSN_SAC_REGEX.test(value.trim());

function EditServiceDescription({ open, setOpen, data, onUpdated }: Props) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<ServiceDescriptionRow>({ ...emptyRow });

  useEffect(() => {
    if (open && data) {
      setState({
        description: data?.description || "",
        hsnSac: data?.hsnSac || "",
        gstRate: data?.gstRate || "",
        serviceFee: data?.serviceFee ?? "",
      });
    }
  }, [open, data]);

  const { mutate, isLoading } = useMutation(updateServiceDescription, {
    onSuccess: () => {
      snack.success("Service Description Updated");
      setOpen(false);
      queryClient.invalidateQueries("service-descriptions");
      onUpdated?.();
    },
    onError: (err: any) => {
      snack.error(handleError(err));
    },
  });

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setState({ ...state, [name]: value });
  };

  const handleSubmit = (e: SubmitType) => {
    e.preventDefault();
    if (!state.description.trim() || !`${state.serviceFee}`.trim()) {
      return snack.error("Please fill Service Description and Service Fee");
    }
    if (state.hsnSac.trim() && !isValidHsnSac(state.hsnSac)) {
      return snack.error("HSN/SAC must be exactly 2, 4 or 6 digits");
    }
    mutate({ id: data?.id, data: state });
  };

  return (
    <DialogWrapper open={open} setOpen={setOpen} title="Update Service Description" width="sm">
      <form onSubmit={handleSubmit}>
        <Box display="flex" flexDirection="column" gap={2}>
          <SafeTextField
            variant="outlined"
            fullWidth
            size="small"
            name="description"
            multiline
            rows={2}
            value={state.description}
            onChange={handleChange}
            label="Service Description"
            required
            inputProps={{ maxLength: 250 }}
          />
          <SafeTextField
            variant="outlined"
            fullWidth
            size="small"
            name="hsnSac"
            value={state.hsnSac}
            onChange={handleChange}
            label="HSN/SAC"
            allowedPattern={/0-9/}
            inputProps={{ maxLength: 6 }}
            error={!!state.hsnSac && !isValidHsnSac(state.hsnSac)}
            helperText={
              !!state.hsnSac && !isValidHsnSac(state.hsnSac) ? "Must be 2, 4 or 6 digits" : ""
            }
          />
          <TextField
            select
            variant="outlined"
            fullWidth
            size="small"
            name="gstRate"
            value={state.gstRate}
            onChange={handleChange}
            label="GST Rate"
          >
            {TAXES.map((tax) => (
              <MenuItem key={tax.value} value={tax.value}>
                {tax.name}
              </MenuItem>
            ))}
          </TextField>
          <SafeTextField
            variant="outlined"
            fullWidth
            size="small"
            type="number"
            name="serviceFee"
            value={state.serviceFee}
            onChange={handleChange}
            onWheel={(e) => (e.target as HTMLElement).blur()}
            label="Service Fee"
            required
            inputProps={{ min: 0, max: 10000000, step: "0.01" }}
          />
        </Box>
        <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
          <LoadingButton
            loading={isLoading}
            disabled={isLoading}
            loadingColor="white"
            width={16}
            size="small"
            title="Update"
            color="secondary"
            type="submit"
          />
        </Box>
      </form>
    </DialogWrapper>
  );
}

export default EditServiceDescription;
